"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MetadataEntry = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const internal_1 = require("../../../internal");
const types_1 = require("../../../types");
const conversion_1 = require("../../../utils/conversion");
const internal_2 = require("../../../utils/internal");
const types_2 = require("./types");
/**
 * Represents an Asset MetadataEntry in the Polymesh blockchain
 */
class MetadataEntry extends internal_1.Entity {
    /**
     * @hidden
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        const { type, id, assetId } = identifiers;
        this.asset = new internal_1.BaseAsset({ assetId }, context);
        this.type = type;
        this.id = id;
        this.set = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.setMetadata, Object.assign(Object.assign({}, args), { metadataEntry: this })] }, context);
        this.clear = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: () => [internal_1.clearMetadata, { metadataEntry: this }], voidArgs: true }, context);
        this.remove = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: () => [internal_1.removeLocalMetadata, { metadataEntry: this }], voidArgs: true }, context);
    }
    /**
     * @hidden
     * Check if a value is of type {@link UniqueIdentifiers}
     */
    static isUniqueIdentifiers(identifier) {
        const { id, assetId, type } = identifier;
        return id instanceof bignumber_js_1.default && typeof assetId === 'string' && type in types_2.MetadataType;
    }
    /**
     * Retrieve name and specs for this MetadataEntry
     */
    details() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataLocalKeyToName, assetMetadataLocalSpecs, assetMetadataGlobalKeyToName, assetMetadataGlobalSpecs, }, }, }, }, id, asset, type, context, } = this;
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
            let rawName, rawSpecs;
            if (type === types_2.MetadataType.Local) {
                [rawName, rawSpecs] = yield Promise.all([
                    assetMetadataLocalKeyToName(rawAssetId, rawId),
                    assetMetadataLocalSpecs(rawAssetId, rawId),
                ]);
            }
            else {
                [rawName, rawSpecs] = yield Promise.all([
                    assetMetadataGlobalKeyToName(rawId),
                    assetMetadataGlobalSpecs(rawId),
                ]);
            }
            return {
                name: (0, conversion_1.bytesToString)(rawName.unwrap()),
                specs: (0, conversion_1.meshMetadataSpecToMetadataSpec)(rawSpecs),
            };
        });
    }
    /**
     * Retrieve the value and details (expiry + lock status) for this MetadataEntry
     *
     * @note - This returns `null` if no value is yet specified for this MetadataEntry
     */
    value() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataValues, assetMetadataValueDetails }, }, }, }, id, type, asset, context, } = this;
            const rawMetadataKey = (0, conversion_1.metadataToMeshMetadataKey)(type, id, context);
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
            const [rawValue, rawValueDetails] = yield Promise.all([
                assetMetadataValues(rawAssetId, rawMetadataKey),
                assetMetadataValueDetails(rawAssetId, rawMetadataKey),
            ]);
            return (0, conversion_1.meshMetadataValueToMetadataValue)(rawValue, rawValueDetails);
        });
    }
    /**
     * Determine whether this MetadataEntry exists on chain
     */
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataGlobalKeyToName, assetMetadataLocalKeyToName }, }, }, }, id, type, asset, context, } = this;
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            let rawName;
            if (type === types_2.MetadataType.Global) {
                rawName = yield assetMetadataGlobalKeyToName(rawId);
            }
            else {
                const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
                rawName = yield assetMetadataLocalKeyToName(rawAssetId, rawId);
            }
            return rawName.isSome;
        });
    }
    /**
     * Check if the MetadataEntry can be modified.
     * A MetadataEntry is modifiable if it exists and is not locked
     */
    isModifiable() {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, type, asset: { id: assetId }, } = this;
            const [exists, metadataValue] = yield Promise.all([this.exists(), this.value()]);
            if (!exists) {
                return {
                    canModify: false,
                    reason: new internal_1.PolymeshError({
                        code: types_1.ErrorCode.DataUnavailable,
                        message: 'Metadata does not exists for the Asset',
                        data: {
                            assetId,
                            type,
                            id,
                        },
                    }),
                };
            }
            if (metadataValue) {
                const { lockStatus } = metadataValue;
                if (lockStatus === types_2.MetadataLockStatus.Locked) {
                    return {
                        canModify: false,
                        reason: new internal_1.PolymeshError({
                            code: types_1.ErrorCode.UnmetPrerequisite,
                            message: 'Metadata is locked and cannot be modified',
                        }),
                    };
                }
                if (lockStatus === types_2.MetadataLockStatus.LockedUntil) {
                    const { lockedUntil } = metadataValue;
                    if (new Date() < lockedUntil) {
                        return {
                            canModify: false,
                            reason: new internal_1.PolymeshError({
                                code: types_1.ErrorCode.UnmetPrerequisite,
                                message: 'Metadata is currently locked',
                                data: {
                                    lockedUntil,
                                },
                            }),
                        };
                    }
                }
            }
            return {
                canModify: true,
            };
        });
    }
    /**
     * Return the MetadataEntry's ID, Asset ticker and Metadata type
     */
    toHuman() {
        const { asset, id, type } = this;
        return (0, internal_2.toHumanReadable)({
            assetId: asset,
            id,
            type,
        });
    }
}
exports.MetadataEntry = MetadataEntry;
//# sourceMappingURL=index.js.map